import { Button } from '@daily/shared/components/Button';
import { Card, CardContent } from '@daily/shared/components/Card';
import { Checkbox } from '@daily/shared/components/Checkbox';
import { Form } from '@daily/shared/components/Form';
import { Stack } from '@daily/shared/components/Stack';
import { Text } from '@daily/shared/components/Text';
import { useParticipantIds } from '@daily-co/daily-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import React, { CSSProperties, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { atom, useRecoilCallback, useRecoilValue } from 'recoil';

import {
  BreakoutRoom,
  BreakoutSession,
  useBreakouts,
} from '/contexts/BreakoutsProvider';
import { useBreakoutView } from '/lib/state/breakouts';

import { ContentContainer } from './ContentContainer';
import { CtaButtonContainer } from './CtaButtonContainer';
import { MAIN_ROOM_IDENTIFIER } from './helpers/constants';
import { useDragAndDrop } from './hooks/useDragAndDrop';
import { useRoomMapping } from './hooks/useRoomMapping';
import { useRooms } from './hooks/useRooms';
import { NumberOfRoomsField } from './NumberOfRoomsField';
import { Room } from './Room';

const sessionConfigState = atom<BreakoutSession>({
  key: 'breakout-session-config',
  default: {
    autojoin: false,
  },
});

const GAP = 16;

type FormState = 'idle' | 'submitting';

export const ConfigureBreakoutRooms: React.FC = () => {
  const { t } = useTranslation();
  const participantIds = useParticipantIds({});
  const [, setBreakoutView] = useBreakoutView();
  const { setBreakoutHost, setupBreakouts } = useBreakouts();

  const { rooms } = useRooms();
  const { getRoomMappingByRoomId } = useRoomMapping();
  const { dragParticipant, dropParticipant } = useDragAndDrop();

  const [formState, setFormState] = useState<FormState>('idle');

  const sessionConfig = useRecoilValue(sessionConfigState);

  const cancelBreakoutConfiguration = useCallback(() => {
    setBreakoutHost('');
    setBreakoutView('start');
  }, [setBreakoutHost, setBreakoutView]);

  const handleChangeAutojoin = useRecoilCallback(
    ({ set }) =>
      (ev: React.ChangeEvent<HTMLInputElement>) => {
        set(sessionConfigState, (config) => ({
          ...config,
          autojoin: ev.target.checked,
        }));
      },
    []
  );

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    setFormState('submitting');

    const roomMapping: Record<string, BreakoutRoom> = rooms.reduce(
      (result, roomId) => {
        return {
          ...result,
          [roomId]: {
            name: rooms.indexOf(roomId) + 1,
            users: getRoomMappingByRoomId(roomId),
          },
        };
      },
      {}
    );

    await setupBreakouts({ ...roomMapping }, sessionConfig);
    setBreakoutView('ongoing');
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rooms.length + 3,
    estimateSize: (index) => {
      /**
       * These are initial estimates for the very first render cycle.
       * Once each element has rendered at least once, these will be
       * replaced by their actual heights.
       */
      switch (index) {
        case 0:
          // Header
          return 32 + GAP;
        case 1:
          // Configuration Card
          return 202 + GAP;
        case 2:
          // MainRoom Card
          return 267 + GAP;
        default:
          // Room Cards
          return 117 + GAP;
      }
    },
    getScrollElement: () => scrollRef.current,
    overscan: 2,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <>
      <ContentContainer
        ref={scrollRef}
        innerHeight={virtualizer.getTotalSize()}
      >
        <Form id="configure-breakout-rooms" onSubmit={handleSubmit}>
          {virtualItems.map((item) => {
            const style: CSSProperties = {
              transform: `translateY(${item.start}px)`,
            };
            switch (item.index) {
              case 0:
                return (
                  <header
                    key="header"
                    className="heading row"
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    style={style}
                  >
                    <Text El="h1" variant="large">
                      {t('breakouts.sessionSetup')}
                    </Text>
                    <Button
                      id="breakouts-cancel-configuration"
                      variant="secondary"
                      onClick={cancelBreakoutConfiguration}
                    >
                      {t('general.cancel')}
                    </Button>
                  </header>
                );
              case 1:
                return (
                  <div
                    key="config"
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="row"
                    style={style}
                  >
                    <Card shadow="none" spacing={12}>
                      <CardContent>
                        <Stack gap={8}>
                          <NumberOfRoomsField participantIds={participantIds} />
                          <Checkbox
                            id="br-autojoin"
                            name="autojoin"
                            label={t('breakouts.autojoinLabel')}
                            checked={sessionConfig?.autojoin}
                            onChange={handleChangeAutojoin}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </div>
                );
              case 2:
                return (
                  <div
                    key="main-room"
                    id="main-room-config"
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="row"
                    style={style}
                  >
                    <Room
                      onDragOver={dragParticipant}
                      onDrop={dropParticipant}
                      onResize={() =>
                        virtualizer.measureElement(
                          document.getElementById('main-room-config')
                        )
                      }
                      roomIdentifier={MAIN_ROOM_IDENTIFIER}
                    />
                  </div>
                );
              default:
                const index = item.index - 3;
                const roomId = rooms[index];
                const domId = `room-config-${roomId}`;
                return (
                  <div
                    key={roomId}
                    id={domId}
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                    className="row"
                    style={style}
                  >
                    <Room
                      onDragOver={dragParticipant}
                      onDrop={dropParticipant}
                      onResize={() =>
                        virtualizer.measureElement(
                          document.getElementById(domId)
                        )
                      }
                      roomIdentifier={roomId}
                      roomName={index + 1}
                    />
                  </div>
                );
            }
          })}
        </Form>
      </ContentContainer>
      <CtaButtonContainer>
        <Button
          id="breakouts-start-session"
          loading={formState === 'submitting'}
          type="submit"
          fullWidth
          form="configure-breakout-rooms"
        >
          {t('breakouts.startBreakoutSession')}
        </Button>
      </CtaButtonContainer>
      <style jsx>{`
        .heading {
          align-items: center;
          display: flex;
          justify-content: space-between;
        }
        .row {
          left: 0;
          padding-bottom: ${GAP}px;
          position: absolute;
          top: 0;
          width: 100%;
        }
      `}</style>
    </>
  );
};
